import { useEffect, useState } from "react";
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import AddModal from "../components/Dynamics/AddModal";
import TicketPresetModal from "../components/Dynamics/TicketPresetModal";
import { pb, generateContactPassword } from "../api/pocketbase";

const AUTHORIZED_USERS_COMPANY_ID = "tt3pm2e5413r7p5";
const AUTHORIZED_USERS_FILTER = ` IsATraqAdmin=true && (isDeleted=false || isDeleted="")`;

const NEWS_VISIBILITY_FIELDS = [
    { key: "ShowInAtraq", label: "Show in Atraq" },
    { key: "ShowInAUP", label: "Show on portal" },
    { key: "ShowInAAP", label: "Show on Citadel" },
    { key: "ShowOnWebsite", label: "Show on website" },
];

const hasAtLeastOneNewsVisibility = (data) =>
    NEWS_VISIBILITY_FIELDS.some(({ key }) => data[key] === true);

const formatDateForInput = (value) => {
    if (!value) return "";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return "";
    }

    return date.toISOString().split("T")[0];
};

const DynamicsPage = () => {
    pb.autoCancellation(false);
    const [modalData, setModalData] = useState(null);
    const [presetModalData, setPresetModalData] = useState(null);
    const [authorizedUsers, setAuthorizedUsers] = useState([]);
    const [authorizedUserSearch, setAuthorizedUserSearch] = useState("");
    const [news, setNews] = useState([]);
    const [contracts, setContracts] = useState([]);
    const [ticketPresets, setTicketPresets] = useState([]);

    const fetchData = async (collection, filter = "") => {
        try {
            const apiResponse = await pb.collection(collection).getFullList({ filter });
            return apiResponse;
        } catch (error) {
            console.error(`Error fetching data from ${collection}:`, error);
            return [];
        }
    };

    useEffect(() => {
        const fetchAllData = async () => {
            const [users, newsList, contractsList, presetsList] = await Promise.all([
                fetchData("Contacts", AUTHORIZED_USERS_FILTER),
                fetchData("News"),
                fetchData("Contracts"),
                fetchData("Ticket_Presets"),
            ]);
            setAuthorizedUsers(users);
            setNews(newsList);
            setContracts(contractsList);
            setTicketPresets(presetsList);
        };
        fetchAllData();
    }, []);

    const refreshCollection = async (collection) => {
        if (collection === "Contacts") {
            setAuthorizedUsers(await fetchData("Contacts", AUTHORIZED_USERS_FILTER));
        } else if (collection === "News") {
            setNews(await fetchData("News"));
        } else if (collection === "Contracts") {
            setContracts(await fetchData("Contracts"));
        } else if (collection === "Ticket_Presets") {
            setTicketPresets(await fetchData("Ticket_Presets"));
        }
    };

    const getSectionFields = (collection) => {
        if (collection === "Contacts") {
            return [
                { key: "Name", label: "Name", type: "text", required: true },
                { key: "Surname", label: "Surname", type: "text", required: true },
                { key: "email", label: "Email", type: "email", required: true },
                { key: "MobileNumber", label: "Mobile Number", type: "text", required: true },
            ];
        }

        if (collection === "News") {
            return [
                { key: "Headline", label: "Headline", type: "text", required: true },
                { key: "News", label: "News", type: "textarea", required: true },
                ...NEWS_VISIBILITY_FIELDS.map(({ key, label }) => ({
                    key,
                    label,
                    type: "checkbox",
                })),
            ];
        }

        if (collection === "Contracts") {
            return [
                { key: "contractName", label: "Contract Name", type: "text", required: true },
                { key: "startDate", label: "Start Date", type: "date" },
                { key: "endDate", label: "End Date", type: "date" },
            ];
        }

        return [];
    };

    const openCreateModal = (title, collection) => {
        setModalData({
            title,
            collection,
            fields: getSectionFields(collection),
            initialValues: {},
            submitLabel: "Save",
        });
    };

    const openEditModal = (title, collection, row) => {
        const initialValues =
            collection === "Contracts"
                ? {
                    contractName: row.contractName || "",
                    startDate: formatDateForInput(row.startDate),
                    endDate: formatDateForInput(row.endDate),
                }
                : collection === "News"
                ? {
                    Headline: row.Headline || row.title || "",
                    News: row.News || row.content || "",
                    ShowInAtraq: !!row.ShowInAtraq,
                    ShowInAUP: !!row.ShowInAUP,
                    ShowInAAP: !!row.ShowInAAP,
                    ShowOnWebsite: !!row.ShowOnWebsite,
                }
                : {
                    Name: row.Name || "",
                    Surname: row.Surname || "",
                    email: row.email || "",
                    MobileNumber: row.MobileNumber || "",
                };

        setModalData({
            title: `Edit ${title}`,
            collection,
            recordId: row.id,
            fields: getSectionFields(collection),
            initialValues,
            submitLabel: "Update",
        });
    };

    const buildPayload = (collection, data, isEdit = false) => {
        if (collection === "Contacts") {
            const generatedPassword = generateContactPassword();
            return {
                Name: data.Name?.trim() || "",
                Surname: data.Surname?.trim() || "",
                email: data.email?.trim() || "",
                MobileNumber: data.MobileNumber?.trim() || "",
                CompanyID: AUTHORIZED_USERS_COMPANY_ID,
                IsATraqAdmin: true,
                emailVisibility: true,
                CanAccessPortal: true,
                ...(isEdit ? {} : { isDeleted: false }),
                ...(isEdit ? {} : {
                    password: generatedPassword,
                    passwordConfirm: generatedPassword,
                    generatedPassword,
                }),
            };
        }

        if (collection === "News") {
            return {
                Headline: data.Headline?.trim() || "",
                News: data.News?.trim() || "",
                ShowInAtraq: !!data.ShowInAtraq,
                ShowInAUP: !!data.ShowInAUP,
                ShowInAAP: !!data.ShowInAAP,
                ShowOnWebsite: !!data.ShowOnWebsite,
            };
        }

        if (collection === "Contracts") {
            return {
                contractName: data.contractName?.trim() || "",
                startDate: data.startDate || "",
                endDate: data.endDate || "",
            };
        }

        return data;
    };

    const handleSaveRecord = async (collection, data, recordId = null) => {
        try {
            if (collection === "News" && !hasAtLeastOneNewsVisibility(data)) {
                toast.error("Select at least one place to show this news.");
                throw new Error("News visibility required");
            }

            const payload = buildPayload(collection, data, Boolean(recordId));
            const generatedPassword = payload.generatedPassword;
            delete payload.generatedPassword;

            if (recordId) {
                await pb.collection(collection).update(recordId, payload);
                toast.success("Record updated successfully.");
            } else {
                await pb.collection(collection).create(payload);
                toast.success(generatedPassword
                    ? `Record created. Password: ${generatedPassword}`
                    : "Record created successfully.");
            }

            setModalData(null);
            await refreshCollection(collection);
        } catch (error) {
            console.error("Error saving data:", error);
            toast.error("Failed to save record.");
        }
    };

    const handleSavePreset = async (data) => {
        try {
            if (data.id) {
                await pb.collection("Ticket_Presets").update(data.id, {
                    preset_name: data.preset_name,
                    preset: data.preset,
                });
                toast.success("Preset updated successfully.");
            } else {
                await pb.collection("Ticket_Presets").create(data);
                toast.success("Preset created successfully.");
            }

            setPresetModalData(null);
            await refreshCollection("Ticket_Presets");
        } catch (error) {
            console.error("Error saving preset:", error);
            toast.error("Failed to save preset.");
            throw error;
        }
    };

    const handleDeleteRecord = async (collection, row) => {
        const label = row.Name || row.title || row.contractName || row.preset_name || "this record";
        const confirmed = window.confirm(`Delete ${label}?`);
        if (!confirmed) {
            return;
        }

        try {
            if (collection === "Contacts") {
                await pb.collection("Contacts").update(row.id, { isDeleted: true });
                toast.success("User marked as deleted.");
            } else {
                await pb.collection(collection).delete(row.id);
                toast.success("Record deleted successfully.");
            }
            await refreshCollection(collection);
        } catch (error) {
            console.error("Error deleting record:", error);
            toast.error("Failed to delete record.");
        }
    };

    const renderActionButtons = (onEdit, onDelete) => (row) => (
        <div className="flex items-center gap-2">
            <button
                type="button"
                onClick={() => onEdit(row)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 transition hover:bg-gray-50 hover:text-[#90c740]"
                aria-label="Edit row"
            >
                <PencilSquareIcon className="h-4 w-4" />
            </button>
            <button
                type="button"
                onClick={() => onDelete(row)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-red-600 transition hover:bg-red-50"
                aria-label="Delete row"
            >
                <TrashIcon className="h-4 w-4" />
            </button>
        </div>
    );

    const filteredAuthorizedUsers = authorizedUsers.filter((user) => {
        const query = authorizedUserSearch.trim().toLowerCase();
        if (!query) return true;

        return [user.Name, user.Surname, user.email, user.MobileNumber]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(query));
    });

    // Reusable section renderer — matches the card style used in TicketsPage
    const renderSection = (title, data, columns, onAdd, emptyLabel = "No data available.", headerActions = null) => (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 mb-6">
            {/* Section header */}
            <div className="p-4 sm:p-6 border-b border-gray-100 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h3 className="text-gray-900 font-bold text-lg">{title}</h3>
                    <p className="text-gray-500 text-sm mt-1">{data.length} {data.length === 1 ? "record" : "records"}</p>
                </div>
                <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:justify-end">
                    {headerActions}
                    <button
                        onClick={onAdd}
                        className="inline-flex h-10 items-center justify-center whitespace-nowrap rounded-lg bg-[#90c740] px-4 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#7ebd2e] w-full sm:w-auto"
                    >
                        + Add
                    </button>
                </div>
            </div>

            <div className="space-y-4 p-4 md:hidden">
                {data.length > 0 ? (
                    data.map((row, i) => (
                        <div key={row.id || i} className="rounded-xl border border-gray-200 p-4 shadow-sm">
                            <div className="space-y-3">
                                {columns.map((col) => (
                                    <div key={col.key}>
                                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{col.label}</p>
                                        <div className="mt-1 text-sm text-gray-700 break-words">
                                            {col.render ? col.render(row) : (row[col.key] || "—")}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-400">
                        {emptyLabel}
                    </div>
                )}
            </div>

            {/* Table */}
            <div className="hidden overflow-x-auto md:block">
                <table className="min-w-[720px] w-full">
                    <thead className="bg-gray-50">
                        <tr>
                            {columns.map((col) => (
                                <th key={col.key} className="py-4 px-6 text-left font-semibold text-gray-700 text-sm">
                                    {col.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.length > 0 ? (
                            data.map((row, i) => (
                                <tr key={row.id || i} className="border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200">
                                    {columns.map((col) => (
                                        <td key={col.key} className="py-4 px-6 text-sm text-gray-700">
                                            {col.render ? col.render(row) : (row[col.key] || "—")}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columns.length} className="py-10 px-6 text-center text-sm text-gray-400">
                                    {emptyLabel}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            {/* Header — matches TicketsPage exactly */}
            <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 sm:px-6 md:px-10 py-4 sm:py-6 bg-white shadow-sm rounded-bl-3xl rounded-br-3xl mb-6 sm:mb-8 gap-4 sm:gap-0">
                <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">Dynamics</h1>
            </header>

            {/* Main Content */}
            <main className="flex-1 px-4 sm:px-6 md:px-10 pb-10">

                {/* Authorized Users */}
                {renderSection(
                    "Authorized Users",
                    filteredAuthorizedUsers,
                    [
                        { key: "Name", label: "Name" },
                        { key: "Surname", label: "Surname" },
                        { key: "email", label: "Email" },
                        { key: "MobileNumber", label: "Mobile Number" },
                        {
                            key: "actions",
                            label: "Actions",
                            render: renderActionButtons(
                                (row) => openEditModal("Authorized User", "Contacts", row),
                                (row) => handleDeleteRecord("Contacts", row)
                            ),
                        },
                    ],
                    () => openCreateModal("Authorized Users", "Contacts"),
                    "No authorized users found.",
                    <input
                        type="text"
                        value={authorizedUserSearch}
                        onChange={(e) => setAuthorizedUserSearch(e.target.value)}
                        placeholder="Search users..."
                        className="h-10 w-full rounded-lg border border-gray-300 bg-white px-4 text-sm text-gray-700 placeholder-gray-400 focus:border-[#90c740] focus:outline-none focus:ring-2 focus:ring-[#90c740] sm:w-72"
                    />
                )}

                {/* News */}
                {renderSection(
                    "News",
                    news,
                    [
                        { key: "Headline", label: "Headline" },
                        {
                            key: "News",
                            label: "News",
                            render: (row) => (
                                <span className="block max-w-sm truncate text-gray-500">
                                    {row.News || row.content || "—"}
                                </span>
                            ),
                        },
                        {
                            key: "visibility",
                            label: "Visibility",
                            render: (row) => {
                                const tags = NEWS_VISIBILITY_FIELDS.filter(({ key }) => row[key]).map(
                                    ({ label }) => label
                                );
                                return (
                                    <span className="text-sm text-gray-600">
                                        {tags.length ? tags.join(", ") : "—"}
                                    </span>
                                );
                            },
                        },
                        {
                            key: "actions",
                            label: "Actions",
                            render: renderActionButtons(
                                (row) => openEditModal("News", "News", row),
                                (row) => handleDeleteRecord("News", row)
                            ),
                        },
                    ],
                    () => openCreateModal("News", "News"),
                    "No news entries found."
                )}

                {/* Contracts */}
                {renderSection(
                    "Contracts",
                    contracts,
                    [
                        { key: "contractName", label: "Contract Name" },
                        { key: "startDate", label: "Start Date", render: (row) => row.startDate ? new Date(row.startDate).toLocaleDateString() : "—" },
                        { key: "endDate", label: "End Date", render: (row) => row.endDate ? new Date(row.endDate).toLocaleDateString() : "—" },
                        {
                            key: "actions",
                            label: "Actions",
                            render: renderActionButtons(
                                (row) => openEditModal("Contract", "Contracts", row),
                                (row) => handleDeleteRecord("Contracts", row)
                            ),
                        },
                    ],
                    () => openCreateModal("Contracts", "Contracts"),
                    "No contracts found."
                )}

                {/* Ticket Presets */}
                {renderSection(
                    "Ticket Presets",
                    ticketPresets,
                    [
                        { key: "preset_name", label: "Preset Name" },
                        {
                            key: "preset",
                            label: "Content Preview",
                            render: (row) => (
                                <span className="block max-w-sm truncate text-gray-500">
                                    {row.preset?.replace(/<[^>]*>/g, "") || "—"}
                                </span>
                            ),
                        },
                        {
                            key: "created",
                            label: "Created",
                            render: (row) => new Date(row.created).toLocaleDateString(),
                        },
                        {
                            key: "actions",
                            label: "Actions",
                            render: renderActionButtons(
                                (row) => setPresetModalData({ initialData: row }),
                                (row) => handleDeleteRecord("Ticket_Presets", row)
                            ),
                        },
                    ],
                    () => setPresetModalData({ initialData: null }),
                    "No presets yet. Click + Add to create your first reply preset."
                )}

            </main>

            {/* Modals */}
            {modalData && (
                <AddModal
                    title={modalData.title}
                    fields={modalData.fields}
                    initialValues={modalData.initialValues}
                    submitLabel={modalData.submitLabel}
                    onSave={(newData) => handleSaveRecord(modalData.collection, newData, modalData.recordId)}
                    onClose={() => setModalData(null)}
                />
            )}

            {presetModalData && (
                <TicketPresetModal
                    onSave={handleSavePreset}
                    initialData={presetModalData.initialData}
                    onClose={() => setPresetModalData(null)}
                />
            )}
        </div>
    );
};

export default DynamicsPage;
